/**
 * sessionSummaryGenerator.js — Génération du résumé session en langage naturel
 *
 * Prend les stats de session + les readings + les templates,
 * retourne un tableau de phrases formant le résumé.
 *
 * Les templates sont chargés depuis l'API (/api/sessions/summary-templates)
 * qui retourne les valeurs DB si modifiées, sinon les valeurs par défaut JSON.
 *
 * Aucune citation nommée dans les templates — formulations génériques uniquement.
 */
import pmData from '../constants/pmThresholds.json';
import { classifyPm } from '../constants/classifyPm';
import { formatHumanDuration } from '../utils/formatDuration';
import { formatDateLocale, formatNumberLocale } from '../utils/formatters';
import i18n from '../i18n';

const T = pmData.PM_THRESHOLDS;
const TMP = pmData.TEMP_THRESHOLDS;
const HUM = pmData.HUMIDITY_THRESHOLDS;

// MAPPER (B2) : palier classifyPm → clé de template existante. Le contrat template
// (/api/sessions/summary-templates, clés good/moderate/bad) reste INCHANGÉ.
const LEVEL_TO_TPLKEY = { bon: 'good', a_surveiller: 'moderate', eleve: 'bad' };

/** Remplace les variables {key} dans un template par leurs valeurs */
function fill(template, vars) {
  if (!template) return '';
  return template.replace(/\{(\w+)\}/g, (_, key) => {
    const v = vars[key];
    return v !== undefined && v !== null ? v : `{${key}}`;
  });
}

/** Clé template PM2.5 (good/moderate/bad) via classifyPm. Garde avg==null → null. */
function pm25Level(avg) {
  if (avg == null) return null;   // pas de phrase « good » sur donnée absente
  return LEVEL_TO_TPLKEY[classifyPm(avg, 'pm25').level] ?? null;
}

/** Clé template PM10 via classifyPm. Garde avg==null → null. */
function pm10Level(avg) {
  if (avg == null) return null;
  return LEVEL_TO_TPLKEY[classifyPm(avg, 'pm10').level] ?? null;
}

/** Niveau température */
function tempLevel(avg) {
  if (avg == null) return null;
  if (avg >= TMP.normalMin && avg <= TMP.normalMax) return 'normal';
  if (avg >= TMP.alertLow  && avg <= TMP.alertHigh) return 'warning';
  return 'alert';
}

/** Niveau humidité */
function humLevel(avg) {
  if (avg == null) return null;
  if (avg >= HUM.normalMin && avg <= HUM.normalMax) return 'normal';
  if (avg >= HUM.alertLow  && avg <= HUM.alertHigh) return 'warning';
  return 'alert';
}

/** Catégorie de durée */
function durationLevel(minutes) {
  if (!minutes) return 'normal';
  if (minutes < 30) return 'short';
  if (minutes > 240) return 'long';
  return 'normal';
}

/** Détecte les pics PM2.5 : groupes de lectures consécutives > warningMax */
function detectPeaks(readings) {
  const threshold = T.pm25.warningMax;
  const peaks = [];
  let inPeak = false, peakMax = 0, peakTime = null;

  for (const r of readings) {
    const v = r.pm2_5;
    if (v != null && v > threshold) {
      if (!inPeak) { inPeak = true; peakMax = v; peakTime = r.timestamp; }
      else if (v > peakMax) { peakMax = v; peakTime = r.timestamp; }
    } else {
      if (inPeak) {
        peaks.push({ value: peakMax, time: peakTime });
        inPeak = false; peakMax = 0; peakTime = null;
      }
    }
  }
  if (inPeak) peaks.push({ value: peakMax, time: peakTime });
  return peaks;
}

// Valeur absente dans une phrase : libellé lisible, jamais de tiret long
// (puces reprises telles quelles dans le PDF, 10.5.303).
function missingValue() {
  return i18n.t('common.notProvided');
}

// Séparateur décimal de la langue active, comme les tableaux et les
// recommandations du PDF : virgule en FR, point en EN.
function fmtValue(value, decimals = 1) {
  return formatNumberLocale(value, decimals) ?? missingValue();
}

/**
 * Date et heure d'un pic dans la langue courante (« 24/10 14:05 ») : l'heure
 * seule serait ambiguë sur une session ou une période qui traverse minuit.
 */
function fmtTime(ts) {
  if (!ts) return missingValue();
  const date = formatDateLocale(ts, { day: '2-digit', month: '2-digit' });
  const time = formatDateLocale(ts, { hour: '2-digit', minute: '2-digit' });
  return `${date} ${time}`;
}

/** % du temps au-dessus d'un seuil ; null sans mesure (jamais « 0 % » sans donnée, D190). */
function pctAbove(readings, field, threshold) {
  const vals = readings.map(r => r[field]).filter(v => v != null);
  if (!vals.length) return null;
  return Math.round((vals.filter(v => v > threshold).length / vals.length) * 100);
}

// Une variable sans valeur (ex. {pctAbove} sans mesure) retire la seule phrase
// qui la porte, plutôt que d'afficher l'accolade.
function dropUnresolved(text) {
  return text.replace(/[^.]*\{\w+\}[^.]*\.?/g, '').trim();
}

// Bloc `period` des modèles : variantes « période » des phrases qui disent
// « session » (durée, PM2.5 satisfaisant, conclusion). Clés à plat pour
// l'éditeur super_admin : durationShort, pm25Good, conclusionBad…
const capitalize = (key) => key.charAt(0).toUpperCase() + key.slice(1);
function scoped(tpl, scope, category, level) {
  const own = scope === 'period' ? tpl.period?.[`${category}${capitalize(level)}`] : null;
  return own || tpl[category]?.[level];
}

/**
 * Niveau de conclusion global, fondé sur les seuls polluants mesurés.
 * Ni PM2.5 ni PM10 mesurés → null : aucune conclusion (jamais « good » sans mesure, D190).
 */
function overallLevel(l25, l10) {
  if (l25 === null && l10 === null) return null;
  if (l25 === 'bad' || l10 === 'bad') return 'bad';
  if (l25 === 'moderate' || l10 === 'moderate') return 'moderate';
  return 'good';
}

/**
 * Génère le résumé complet.
 * @param {Object} session   — données de session (pm2_5_avg, pm10_avg, temp_avg, etc.),
 *                             ou session fenêtrée d'une période (periodAnalysis)
 * @param {Array}  readings  — tableau de mesures PM (pour la détection de pics)
 * @param {Object} templates — templates chargés depuis l'API
 * @param {{scope?: 'session'|'period'}} [options] — 'period' : formulations « période »
 * @returns {string[]}       — tableau de phrases (une par thème)
 */
export function generateSummary(session, readings = [], templates = {}, { scope = 'session' } = {}) {
  if (!session) return [];

  const sentences = [];
  const tpl = templates;

  // ─── Durée ────────────────────────────────────────────────────────────────
  // Catégorie : on garde duration_min (cohérent avec les seuils en minutes).
  // Affichage : on lit end_time - start_time pour la précision aux secondes.
  // Bornes absentes : pas de phrase (formatHumanDuration rendrait un tiret long).
  const durLvl = durationLevel(session.duration_min);
  const durationMs = session.end_time && session.start_time
    ? new Date(session.end_time) - new Date(session.start_time)
    : null;
  const durationTpl = scoped(tpl, scope, 'duration', durLvl);
  if (durationTpl && durationMs !== null && durationMs >= 0) {
    sentences.push(fill(durationTpl, { duration: formatHumanDuration(durationMs) }));
  }

  // ─── PM2.5 ────────────────────────────────────────────────────────────────
  const l25 = pm25Level(session.pm2_5_avg);
  const pm25Tpl = l25 && scoped(tpl, scope, 'pm25', l25);
  if (pm25Tpl) {
    const pct = pctAbove(readings, 'pm2_5', T.pm25.alertMax);
    sentences.push(dropUnresolved(fill(pm25Tpl, {
      value:    fmtValue(session.pm2_5_avg),
      max:      fmtValue(session.pm2_5_max),
      pctAbove: pct,
    })));
  }

  // ─── Pics PM2.5 ───────────────────────────────────────────────────────────
  // « Aucun pic » n'est affirmé que si PM2.5 a été mesuré au moins une fois.
  const pm25Measured = readings.some(r => typeof r.pm2_5 === 'number' && Number.isFinite(r.pm2_5));
  if (pm25Measured && tpl.peaks) {
    const peaks = detectPeaks(readings);
    if (peaks.length === 0) {
      sentences.push(tpl.peaks.none || '');
    } else if (peaks.length === 1) {
      sentences.push(fill(tpl.peaks.one, {
        count:     1,
        peakValue: fmtValue(peaks[0].value),
        peakTime:  fmtTime(peaks[0].time),
      }));
    } else {
      const maxPeak = peaks.reduce((a, b) => a.value > b.value ? a : b);
      sentences.push(fill(tpl.peaks.multiple, {
        count:     peaks.length,
        peakValue: fmtValue(maxPeak.value),
        peakTime:  fmtTime(maxPeak.time),
      }));
    }
  }

  // ─── PM10 ─────────────────────────────────────────────────────────────────
  const l10 = pm10Level(session.pm10_avg);
  if (l10 && l10 !== 'good' && tpl.pm10?.[l10]) {
    sentences.push(fill(tpl.pm10[l10], {
      value: fmtValue(session.pm10_avg),
      max:   fmtValue(session.pm10_max),
    }));
  }

  // ─── Température ──────────────────────────────────────────────────────────
  const lTemp = tempLevel(session.temp_avg);
  if (lTemp && lTemp !== 'normal' && tpl.temperature?.[lTemp]) {
    sentences.push(fill(tpl.temperature[lTemp], { value: fmtValue(session.temp_avg) }));
  }

  // ─── Humidité ─────────────────────────────────────────────────────────────
  const lHum = humLevel(session.humidity_avg);
  if (lHum && lHum !== 'normal' && tpl.humidity?.[lHum]) {
    sentences.push(fill(tpl.humidity[lHum], { value: fmtValue(session.humidity_avg, 0) }));
  }

  // ─── Conclusion ───────────────────────────────────────────────────────────
  // Un seul polluant mesuré : la conclusion est précédée d'une réserve qui
  // nomme le polluant manquant, pour ne pas la présenter comme complète.
  const overall = overallLevel(l25, l10);
  const conclusionTpl = overall && scoped(tpl, scope, 'conclusion', overall);
  if (conclusionTpl) {
    if (l25 === null || l10 === null) {
      sentences.push(i18n.t('session.summary.partialConclusion', {
        missing:  l25 === null ? 'PM2.5' : 'PM10',
        measured: l25 === null ? 'PM10' : 'PM2.5',
      }));
    }
    sentences.push(conclusionTpl);
  }

  return sentences.filter(Boolean);
}
